package fr.frux.shared.data.favorites

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.shared.data.local.FavoriteLocalDataSource
import fr.frux.shared.data.local.NoOpFavoriteLocalDataSource
import fr.frux.shared.domain.favorites.FavoritesStore
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update

/**
 * La persistance locale (#61) est un cache de la vérité serveur, jamais une file de
 * synchronisation : un toggle n'y entre qu'après confirmation réseau, et un échec de la base
 * ne casse jamais l'opération en cours — l'état en mémoire suit le verdict du serveur, le
 * cache se resynchronisera au prochain refresh réussi.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class FavoritesStoreImpl(
    private val treeRepository: TreeRepository,
    private val localDataSource: FavoriteLocalDataSource = NoOpFavoriteLocalDataSource,
) : FavoritesStore {
    private val _favoriteIds = MutableStateFlow<Set<Long>>(emptySet())
    override val favoriteIds: StateFlow<Set<Long>> = _favoriteIds.asStateFlow()

    // Incrémentée par clear() : capturée par toggle()/refresh() avant l'appel réseau, elle permet
    // de détecter un clear() survenu pendant le vol (ex. déconnexion) et de ne pas rejouer le
    // rollback ou une écriture cache d'un appel lancé pour un autre utilisateur.
    private var epoch = 0

    // Ids dont le toggle optimiste n'est pas encore confirmé par le réseau : seed() (typiquement
    // le GET du détail d'un arbre) et l'application d'un instantané (cache ou serveur) doivent
    // les ignorer, sinon un état périmé peut doubler un toggle en vol et écraser son résultat.
    private val inFlight = mutableSetOf<Long>()

    override suspend fun refresh(): Result<Unit> {
        val epochAtCall = epoch
        // Cache d'abord : les favoris survivent au redémarrage et s'affichent même hors ligne,
        // le réseau ne fait ensuite que corriger. Une base en échec se traite comme vide.
        runCatchingCancellable { localDataSource.favoriteIds() }
            .onSuccess { cached -> if (epoch == epochAtCall) applySnapshot(cached) }
            .onFailure { Logger.e(TAG, "⚠️ Lecture du cache des favoris échouée", it) }
        return treeRepository.getFavoriteTrees().first().map { trees ->
            if (epoch != epochAtCall) return@map
            applySnapshot(trees.map { it.id }.toSet())
            persistSnapshot(_favoriteIds.value)
        }
    }

    /**
     * Remplace l'appartenance par [snapshot], sauf pour les ids en vol : un instantané (cache au
     * démarrage, réponse serveur du refresh) ne doit jamais écraser un toggle optimiste non
     * confirmé — c'est le rollback ou la confirmation de ce toggle qui tranchera.
     */
    private fun applySnapshot(snapshot: Set<Long>) {
        _favoriteIds.update { current -> (snapshot - inFlight) + current.intersect(inFlight) }
    }

    override suspend fun toggle(treeId: Long): Result<Unit> {
        val target = treeId !in _favoriteIds.value
        val epochAtCall = epoch
        inFlight += treeId
        _favoriteIds.update { if (target) it + treeId else it - treeId }
        return try {
            treeRepository
                .setFavorite(treeId, target)
                .onSuccess {
                    // Persisté seulement une fois confirmé par le serveur (le cache n'est pas une
                    // file offline), et seulement si aucun clear() n'a eu lieu pendant le vol.
                    if (epoch == epochAtCall) {
                        runCatchingCancellable { localDataSource.setFavorite(treeId, target) }
                            .onFailure { Logger.e(TAG, "⚠️ Persistance du favori $treeId échouée", it) }
                    }
                }.onFailure {
                    // Rollback de la seule appartenance fautive, jamais d'un instantané complet :
                    // un toggle concurrent réussi entre-temps ne doit pas être écrasé. Mais seulement
                    // si aucun clear() n'a eu lieu depuis l'appel : sinon on ré-ajouterait l'id sur le
                    // set vidé d'un utilisateur qui s'est déconnecté pendant le vol.
                    if (epoch == epochAtCall) {
                        _favoriteIds.update { ids -> if (target) ids - treeId else ids + treeId }
                    }
                }
        } finally {
            inFlight -= treeId
        }
    }

    override fun seed(
        treeId: Long,
        isFavorite: Boolean,
    ) {
        // No-op si un toggle est en vol pour cet id : un GET concurrent peut renvoyer l'état
        // serveur périmé et écraser le résultat du toggle une fois celui-ci confirmé.
        if (treeId in inFlight) return
        _favoriteIds.update { if (isFavorite) it + treeId else it - treeId }
    }

    override fun seedAll(ids: Collection<Long>) {
        _favoriteIds.update { it + (ids - inFlight) }
    }

    override suspend fun clear() {
        epoch++
        _favoriteIds.value = emptySet()
        // La mémoire d'abord : même si la base échoue, aucun favori de l'ancien compte ne reste
        // affiché. Le résidu en base sera écrasé par le replaceAll du prochain refresh réussi.
        runCatchingCancellable { localDataSource.clear() }
            .onFailure { Logger.e(TAG, "⚠️ Purge du cache des favoris échouée", it) }
    }

    private suspend fun persistSnapshot(ids: Set<Long>) {
        runCatchingCancellable { localDataSource.replaceAll(ids) }
            .onFailure { Logger.e(TAG, "⚠️ Écriture du cache des favoris échouée", it) }
    }

    private companion object {
        const val TAG = "FavoritesStore"
    }
}
